import React, { useState, useRef, useEffect, useCallback } from 'react';
import APIService from '../services/api';
import { avecCle } from '../services/cleLecture';
import { useConfirm } from './ConfirmDialog';
import LotConfirmation from './LotConfirmation';
import './AgentChat.css';
import NuageReflexion from './NuageReflexion';
import { ancre } from '../tutoriel/ancres';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const STORAGE_KEY = 'agentchat.history.v1';

// Libellés d'outil → indicateur lisible, sobre (pas d'emojis)
const TOOL_LABELS = {
  search_candidats: 'Recherche de candidats…',
  search_candidates: 'Recherche de candidats…',
  search_besoins: 'Recherche de besoins…',
  search: 'Recherche…',
  matching: 'Matching en cours…',
  // Le back expose nom + description, pas de label : le libellé se pose ici.
  sourcing_suivi_lire: 'Consultation du suivi des campagnes…',
  frais_justificatif_ouvrir: 'Préparation du lien du justificatif…',
  ressource_document_ouvrir: 'Préparation du document…',
  default: 'Action en cours…',
};
// Le catalogue d'outils vit côté serveur et s'étoffe : on déduit le libellé du
// nom de l'outil plutôt que d'afficher « Recherche… » sur une écriture.
const TOOL_PATTERNS = [
  [/scan|rescan|kdrive/, 'Analyse du dossier kDrive…'],
  [/matching|score/, 'Matching en cours…'],
  [/cv.*(apply|améli|ameli|improve)/, 'Amélioration du CV…'],
  [/(generate|generer|générer|docx|contrat|matrice|facture|invoice)/, 'Génération du document…'],
  [/(upload|copy|copier|piece|pièce)/, 'Mise à jour des pièces…'],
  [/(rename|renom)/, 'Renommage…'],
  [/(delete|supprim|remove)/, 'Suppression…'],
  [/(attach|rattach|position|assign)/, 'Rattachement…'],
  [/(mail|email|send|envoi|notify|relance)/, 'Envoi…'],
  [/(search|recherche|list|get|read)/, 'Recherche…'],
];
// Un label envoyé par le serveur peut encore arriver préfixé d'un emoji : on
// le retire à l'affichage — même sobriété quel que soit l'émetteur.
const sansEmoji = (t) => String(t || '').replace(/^[\p{Extended_Pictographic}\u{FE0F}\u{200D}\s]+/gu, '').trim();
const toolLabel = (ev) => {
  if (ev.label) return sansEmoji(ev.label) || TOOL_LABELS.default;
  const name = String(ev.tool || ev.name || '');
  if (TOOL_LABELS[name]) return TOOL_LABELS[name];
  const hit = TOOL_PATTERNS.find(([re]) => re.test(name.toLowerCase()));
  return hit ? hit[1] : TOOL_LABELS.default;
};

// Résultat d'outil « pièce à ouvrir » (frais_justificatif_ouvrir,
// ressource_document_ouvrir…) : le serveur rend url / download_url SANS token
// — choix de sécurité délibéré, le jwt ne transite jamais par la conversation
// — et c'est le front qui appose la clé de lecture À L'OUVERTURE (?cle=, ou &cle= quand
// l'url porte déjà ?download=1), exactement comme pour les aperçus de
// Mon espace. On ne reconnaît une pièce qu'à son descripteur (frais/document)
// ou à justificatif:true — pas de faux positif sur les autres évènements.
const normPiece = (ev) => {
  const p = { ...(ev.payload || ev.data || ev.result || {}), ...ev };
  if (!p.frais && !p.document && !p.doc && p.justificatif !== true) return null;
  const url = typeof p.url === 'string' && /^https?:\/\//.test(p.url) ? p.url : null;
  if (!url) return null; // justificatif absent : le message de l'agent suffit
  const desc = p.frais || p.document || p.doc || {};
  const montant = desc.montant != null ? `${desc.montant} ${desc.devise || 'CHF'}` : null;
  return {
    url,
    download: typeof p.download_url === 'string' && /^https?:\/\//.test(p.download_url) ? p.download_url : null,
    titre: desc.label || desc.fichier || desc.name || 'Document',
    sous: [desc.ressource, montant, desc.label ? desc.fichier : null].filter(Boolean).join(' · '),
  };
};
// 28.09 : la CLÉ DE LECTURE (30 min, lecture seule), jamais le jeton de
// session — et seulement vers NOTRE API : une adresse d'ailleurs ne reçoit rien
// (avant, le jeton partait vers n'importe quel hôte rendu par un outil).
const avecToken = (u) => avecCle(u);

// Normalise un item « résultat » (candidat le plus souvent)
const normResult = (r) => ({
  kind: r.kind || (r.besoin_id || r.besoinId ? 'besoin' : 'candidat'),
  id: r.candidat_id ?? r.candidatId ?? r.besoin_id ?? r.besoinId ?? r.id,
  title: r.nom_complet || (r.prenom || r.nom ? `${r.prenom || ''} ${r.nom || ''}`.trim() : null) || r.titre || r.title || r.label || '—',
  subtitle: r.titre_poste || r.poste || r.titre || r.client || r.ville || r.subtitle || '',
  score: r.score ?? r.match?.score ?? null,
});

/**
 * Une liste de profils arrivée sous un type d'évènement inattendu.
 *
 * Les cartes cliquables ne dépendent pas du nom de l'évènement : dès qu'un lot
 * de candidats (ou de besoins) traverse le flux, il vaut mieux le rendre que le
 * laisser au fil du texte, où l'on ne peut plus cliquer. On exige un
 * identifiant ET un signe de profil (nom, type, score) : un lot de documents ne
 * doit surtout pas devenir une liste de fiches.
 */
const CLES_LOT = ['results', 'candidats', 'candidates', 'profils', 'profiles', 'matches', 'classement', 'items', 'data'];
const estProfil = (x) => !!x && typeof x === 'object'
  && (x.candidat_id ?? x.candidatId ?? x.candidate_id ?? x.besoin_id ?? x.besoinId ?? x.id) != null
  && (x.nom || x.prenom || x.nom_complet || x.kind === 'candidat' || x.kind === 'besoin'
      || x.score != null || x.match?.score != null);
const lotResultats = (ev) => {
  for (const source of [ev, ev?.payload, ev?.data, ev?.result]) {
    if (!source || typeof source !== 'object') continue;
    for (const cle of CLES_LOT) {
      const arr = source[cle];
      if (!Array.isArray(arr) || !arr.length) continue;
      const profils = arr.filter(estProfil);
      if (profils.length) return profils.map(normResult);
    }
  }
  return [];
};

export default function AgentChat({ theme = 'light', onOpenCandidat, onCloseCandidat, onOpenBesoin, onOpenAo, onNavigate, showNotification }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { const raw = sessionStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  const [input, setInput] = useState('');
  // Pièces jointes du composer : uploadées via POST /agent/files (multipart),
  // référencées par file_ids au POST /agent. Références valables 24 h côté back.
  const [attachments, setAttachments] = useState([]); // [{ file_id, name, mime, size }]
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [tool, setTool] = useState(null); // indicateur outil en cours
  // La progression nom par nom d'une tâche suivie (tool_progress → etat :
  // { total, done, elements[] }) : une barre et la liste, servies par le
  // serveur — « CV Jihed en cours », fait, à venir, échec. Jamais recomposée ici.
  const [toolEtat, setToolEtat] = useState(null);
  // L'agent travaille sans rien écrire : avant son premier mot, et quand le
  // flux se tait au milieu d'une réponse (il prépare l'outil suivant — le
  // serveur n'annonce l'outil qu'une fois décidé). Ce second temps n'affichait
  // rien : la phrase restait en plan et l'agent semblait figé.
  const [attente, setAttente] = useState(false);
  const silenceRef = useRef(null);
  const SILENCE_MS = 900;
  const guetterSilence = useCallback(() => {
    clearTimeout(silenceRef.current);
    silenceRef.current = setTimeout(() => setAttente(true), SILENCE_MS);
  }, []);
  useEffect(() => () => clearTimeout(silenceRef.current), []);
  const { confirm, confirmElement } = useConfirm();
  // La création d'un LOT de postes se confirme dans une liste : Besoin ou
  // Vivier devant chaque ligne, une ligne se retire (13.10). La liste ajustée
  // est ce qui part à /agent/confirm ; null = annulé.
  const [lot, setLot] = useState(null);
  const ouvrirLot = useCallback((input) => new Promise(resolve => setLot({ input, resolve })), []);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // File des demandes de confirmation de l'agent (une modale à la fois).
  const fileConfirm = useRef(Promise.resolve());

  // Persistance session
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch { /* quota */ }
  }, [messages]);

  // Auto-scroll en bas
  useEffect(() => {
    const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight;
  }, [messages, tool, streaming, attente]);

  // Focus à l'ouverture
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 60); }, [open]);

  // Fermeture au clavier (Échap)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const dispatchUiAction = useCallback((ev) => {
    // Le backend peut imbriquer sous payload/data/args, ou mettre l'action dans le type.
    // Le `type` explicite PRIME sur `action` : le back joint aussi les champs legacy
    // (action/name/input) au même objet — si leur valeur diffère du nom canonique,
    // elle ne doit pas faire échouer le dispatch d'un évènement pourtant bien typé.
    const p = { ...(ev.payload || ev.data || ev.args || {}), ...ev };
    const KNOWN = ['open_candidat', 'close_candidat', 'close_fiches', 'close_all', 'open_besoin', 'open_ao', 'navigate_to'];
    const action = (KNOWN.includes(ev.type) ? ev.type : null) || p.action || p.ui_action;
    if (!action) return false;
    // Fermer les aperçus : close_candidat + candidat_id = une fiche ;
    // close_candidat sans id, close_fiches ou close_all = toute la pile.
    if (action === 'close_candidat' || action === 'close_fiches' || action === 'close_all') {
      const id = p.candidat_id ?? p.candidatId ?? p.candidateId ?? (action === 'close_candidat' ? p.id : null);
      onCloseCandidat?.(action === 'close_candidat' && id != null ? id : null);
      return true;
    }
    if (action === 'open_candidat') {
      const id = p.candidat_id ?? p.candidatId ?? p.candidateId ?? p.id;
      if (id != null) {
        const meta = {
          title: p.nom_complet || (p.prenom || p.nom ? `${p.prenom || ''} ${p.nom || ''}`.trim() : null) || p.title,
          subtitle: p.titre_poste || p.poste || p.titre || p.subtitle,
          score: p.score ?? p.match?.score,
        };
        onOpenCandidat?.(id, meta);
        return true;
      }
    } else if (action === 'open_besoin') {
      const id = p.besoin_id ?? p.besoinId ?? p.id;
      if (id != null) { onOpenBesoin?.(id); return true; }
    } else if (action === 'open_ao') {
      // Détail d'un dossier AO — id déjà vérifié côté serveur (l'évènement
      // n'arrive que si le dossier existe). titre/statut = vérité base.
      const id = p.ao_id ?? p.aoId ?? p.id;
      if (id != null) { onOpenAo?.(id); return true; }
    } else if (action === 'navigate_to') {
      const to = p.to ?? p.path ?? p.url ?? p.route;
      if (to) { onNavigate?.(to); return true; }
    }
    return false;
  }, [onOpenCandidat, onCloseCandidat, onOpenBesoin, onOpenAo, onNavigate]);

  const appendAssistant = useCallback((text) => {
    if (!text) return;
    setMessages(prev => [...prev, { role: 'assistant', content: text, results: [] }]);
  }, []);

  // confirm_action : l'agent ne modifie rien sans validation → modale de confirmation
  // puis POST /agent/confirm { name, input } (et confirmation_id si l'évènement
  // en porte un).
  const handleConfirmAction = useCallback(async (ev) => {
    const p = { ...(ev.payload || ev.data || {}), ...ev };
    const name = p.name || p.action || p.tool;
    const confId = p.confirmation_id ?? p.confirm_id ?? p.id ?? null;
    const inputData = p.input ?? p.args ?? {};
    const summary = p.summary || p.message || 'Confirmer cette action ?';
    // Jamais d'abandon silencieux : une demande de confirmation illisible doit
    // se VOIR — sinon l'agent affirme « en attente de ta confirmation » et
    // l'utilisateur ne trouve aucune modale (vécu sur la fusion de doublons).
    if (!name && confId == null) {
      appendAssistant('L\'agent demande une confirmation, mais la demande est illisible (identifiant absent) — rien n\'a été fait. Relancez la demande, et si ça se reproduit c\'est un problème côté serveur.');
      return;
    }
    let inputEnvoye = inputData;
    if (name === 'besoins_lot_creer' && Array.isArray(inputData?.lignes)) {
      const choisi = await ouvrirLot(inputData);
      setLot(null);
      if (!choisi) { appendAssistant('Action annulée.'); return; }
      inputEnvoye = choisi;
    } else {
      const ok = await confirm({ title: 'Confirmer l\'action', message: summary, detail: p.detail || p.description || undefined, confirmLabel: 'Confirmer', cancelLabel: 'Annuler', tone: 'primary' });
      if (!ok) { appendAssistant('Action annulée.'); return; }
    }
    try {
      const r = await fetch(`${BASE}/agent/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}` },
        body: JSON.stringify({ ...(name ? { name } : {}), input: inputEnvoye, ...(confId != null ? { confirmation_id: confId } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.('Action effectuée', 'success');
      // Contrat serveur : la réponse porte `message` (ex. « 21 fusions
      // effectuées — 0 échec ») ; `result` conservé en repli historique.
      const texte = typeof j.message === 'string' ? j.message : (typeof j.result === 'string' ? j.result : null);
      appendAssistant(texte || 'Action effectuée.');
    } catch (e) {
      showNotification?.(`Échec : ${e.message}`, 'error');
      appendAssistant(`Échec de l'action : ${e.message}`);
    }
  }, [confirm, ouvrirLot, appendAssistant, showNotification]);

  // Applique un évènement du flux à l'état
  const applyEvent = useCallback((ev) => {
    if (!ev || typeof ev !== 'object') return;
    const type = ev.type || ev.event;
    switch (type) {
      case 'text_delta':
      case 'delta':
      case 'text': {
        const chunk = ev.text ?? ev.delta ?? ev.content ?? '';
        if (!chunk) break;
        setTool(null);
        setAttente(false);
        guetterSilence();
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && last.streaming) {
            next[next.length - 1] = { ...last, content: (last.content || '') + chunk };
          } else {
            next.push({ role: 'assistant', content: chunk, streaming: true, results: [] });
          }
          return next;
        });
        break;
      }
      case 'tool_start':
        clearTimeout(silenceRef.current);
        setTool(toolLabel(ev));
        break;
      // Un travail de fond n'a pas fini : le serveur attend et relance l'outil
      // dans le même tour, et dit où il en est (« Analyse des profils de notre
      // base… scoring 12/25 »). On remplace le libellé, rien d'autre — le
      // copilote parlera avec le résultat.
      case 'tool_progress':
        setTool(toolLabel(ev));
        setToolEtat(ev.etat && typeof ev.etat === 'object' && Array.isArray(ev.etat.elements) ? ev.etat : null);
        break;
      case 'tool_end':
      case 'tool_stop':
        setTool(null);
        setToolEtat(null);
        setAttente(true);
        break;
      case 'result':
      case 'tool_result':
      case 'candidat':
      case 'besoin': {
        const piece = normPiece(ev);
        if (piece) { addPieces([piece]); break; }
        const item = normResult(ev.result || ev.data || ev);
        addResults([item]);
        break;
      }
      case 'results': {
        const arr = (ev.results || ev.data || []).map(normResult);
        addResults(arr);
        break;
      }
      case 'ui_action':
      case 'action':
      case 'open_candidat':
      case 'close_candidat':
      case 'close_fiches':
      case 'close_all':
      case 'open_besoin':
      case 'open_ao':
      case 'navigate_to':
        dispatchUiAction(ev);
        break;
      case 'confirm_action':
      case 'confirm':
        // Sérialisées : useConfirm n'affiche qu'une modale à la fois, et deux
        // demandes simultanées s'écraseraient (perdues sans trace). Un lot de
        // confirmations (21 fusions…) défile donc une par une.
        fileConfirm.current = fileConfirm.current.then(() => handleConfirmAction(ev)).catch(() => {});
        break;
      case 'error':
        showNotification?.(ev.message || 'Erreur de l\'agent', 'error');
        break;
      case 'done':
      case 'end':
        break;
      default:
        // Confirmation arrivée sous un type inattendu (tool_confirmation…) ou
        // reconnaissable à ses champs : la reconnaître plutôt que la perdre.
        if (/confirm/i.test(String(type || '')) || ev.confirmation_id != null || (ev.payload || ev.data || {}).confirmation_id != null) {
          fileConfirm.current = fileConfirm.current.then(() => handleConfirmAction(ev)).catch(() => {});
          break;
        }
        // Pièce à ouvrir arrivée sous un type inattendu : la carte doit se
        // rendre quel que soit l'emballage de l'évènement.
        { const piece = normPiece(ev); if (piece) { addPieces([piece]); break; } }
        // Action éventuellement portée par un évènement non typé
        if (dispatchUiAction(ev)) break;
        // Profils arrivés sous un autre nom d'évènement (matching, scan…) :
        // ils redeviennent des cartes cliquables.
        { const lot = lotResultats(ev); if (lot.length) { addResults(lot); break; } }
        // Payload texte agrégé éventuel
        if (ev.text || ev.content) applyEvent({ type: 'text_delta', text: ev.text || ev.content });
        break;
    }
  }, [dispatchUiAction, handleConfirmAction, showNotification, guetterSilence]);

  const addResults = (items) => {
    const clean = items.filter(r => r.id != null);
    if (!clean.length) return;
    setMessages(prev => {
      const next = [...prev];
      let last = next[next.length - 1];
      if (!last || last.role !== 'assistant') {
        next.push({ role: 'assistant', content: '', streaming: true, results: [] });
        last = next[next.length - 1];
      }
      const existing = last.results || [];
      const merged = [...existing];
      for (const r of clean) if (!merged.some(m => m.kind === r.kind && m.id === r.id)) merged.push(r);
      next[next.length - 1] = { ...last, results: merged };
      return next;
    });
  };

  // Cartes « pièce à ouvrir » attachées au dernier message assistant —
  // même mécanique que addResults, dédoublonnées par url.
  const addPieces = (items) => {
    const clean = items.filter(Boolean);
    if (!clean.length) return;
    setMessages(prev => {
      const next = [...prev];
      let last = next[next.length - 1];
      if (!last || last.role !== 'assistant') {
        next.push({ role: 'assistant', content: '', streaming: true, results: [] });
        last = next[next.length - 1];
      }
      const existing = last.pieces || [];
      const merged = [...existing];
      for (const p of clean) if (!merged.some(m => m.url === p.url)) merged.push(p);
      next[next.length - 1] = { ...last, pieces: merged };
      return next;
    });
  };

  // Upload des pièces jointes : max 15 fichiers / 15 Mo chacun (contrat back).
  const MAX_FILES = 15;
  const MAX_SIZE = 15 * 1024 * 1024;
  const uploadFiles = useCallback(async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const room = MAX_FILES - attachments.length;
    if (room <= 0) { showNotification?.('Maximum 15 fichiers par message.', 'error'); return; }
    const accepted = [];
    for (const f of incoming.slice(0, room)) {
      if (f.size > MAX_SIZE) { showNotification?.(`« ${f.name} » dépasse 15 Mo — ignoré.`, 'error'); continue; }
      accepted.push(f);
    }
    if (incoming.length > room) showNotification?.(`Maximum ${MAX_FILES} fichiers par message — les premiers ont été gardés.`, 'error');
    if (!accepted.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      accepted.forEach(f => fd.append('files', f));
      const r = await fetch(`${BASE}/agent/files`, { method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}` }, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const files = Array.isArray(j.files) ? j.files : [];
      setAttachments(prev => [...prev, ...files]);
    } catch (e) { showNotification?.('Envoi du fichier impossible : ' + e.message, 'error'); }
    finally { setUploading(false); }
  }, [attachments.length, showNotification]);

  const send = useCallback(async () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || streaming) return;
    setInput('');
    const sentFiles = attachments;
    setAttachments([]);
    // Sans texte, on matérialise l'envoi par la liste des fichiers (le back
    // injecte de toute façon les fichiers via file_ids dans le contexte agent).
    const content = text || `Fichiers joints : ${sentFiles.map(f => f.name).join(', ')}`;
    const history = [...messages, { role: 'user', content, files: sentFiles.length ? sentFiles : undefined }];
    setMessages(history);
    setStreaming(true);
    setTool(null);
    setAttente(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // On n'envoie que { role, content } (pas les résultats attachés)
      const payloadMessages = history.map(m => ({ role: m.role, content: m.content || '' })).filter(m => m.content);
      const body = { messages: payloadMessages };
      if (sentFiles.length) body.file_ids = sentFiles.map(f => f.file_id);
      const res = await fetch(`${BASE}/agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${APIService.getToken()}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Trames SSE séparées par une ligne vide ; fallback NDJSON par ligne
        const parts = buf.split(/\r?\n\r?\n/);
        buf = parts.pop();
        for (const part of parts) handleFrame(part);
      }
      if (buf.trim()) handleFrame(buf);
    } catch (e) {
      if (e.name !== 'AbortError') showNotification?.(`Erreur agent : ${e.message}`, 'error');
    } finally {
      setStreaming(false);
      setTool(null);
      clearTimeout(silenceRef.current);
      setAttente(false);
      abortRef.current = null;
      setMessages(prev => prev.map(m => m.streaming ? { ...m, streaming: false } : m));
    }
  }, [input, attachments, streaming, messages, applyEvent, showNotification]); // eslint-disable-line react-hooks/exhaustive-deps

  // Une trame SSE peut contenir plusieurs lignes (event:/data:). La ligne
  // event: porte le TYPE au format SSE standard — la jeter faisait disparaître
  // sans trace tout évènement dont le JSON ne répète pas son type (vécu : la
  // confirmation de fusion « en attente dans l'interface » que personne ne
  // voyait jamais).
  const handleFrame = (frame) => {
    const lines = frame.split(/\r?\n/);
    const datas = [];
    let evName = null;
    for (const line of lines) {
      const l = line.trim();
      if (!l || l.startsWith(':')) continue;
      if (l.startsWith('event:')) { evName = l.slice(6).trim() || null; continue; }
      if (l.startsWith('data:')) datas.push(l.slice(5).trim());
      else if (l.startsWith('{') || l.startsWith('[')) datas.push(l); // NDJSON brut
    }
    for (const d of datas) {
      if (!d || d === '[DONE]') continue;
      let ev; try { ev = JSON.parse(d); } catch { continue; }
      if (evName && ev && typeof ev === 'object' && !Array.isArray(ev) && !ev.type && !ev.event) ev = { ...ev, type: evName };
      applyEvent(ev);
    }
  };

  const stop = () => { abortRef.current?.abort(); };

  const newConversation = () => {
    stop();
    setMessages([]);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="agentchat-root" data-theme={theme}>
      {/* Bouton flottant */}
      {!open && (
        <button {...ancre('copilote')} className="agentchat-fab" onClick={() => setOpen(true)} title="Assistant IA" aria-label="Ouvrir l'assistant">
          <BI icon="chat-square-text" />
        </button>
      )}

      {open && (
        <>
          {/* Drawer non-modal : l'app reste visible pour voir les fiches ouvertes par l'agent */}
          <div className="agentchat-drawer" role="dialog" aria-label="Assistant IA">
            {/* En-tête bleu foncé */}
            <div className="agentchat-head">
              <div className="agentchat-head-title">
                <span className="agentchat-head-ic"><BI icon="chat-square-text" /></span>
                <div>
                  <div className="agentchat-head-h">Assistant</div>
                  <div className="agentchat-head-sub">Recherche & actions guidées</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="agentchat-head-btn" onClick={newConversation} title="Nouvelle conversation"><BI icon="plus-lg" /></button>
                <button className="agentchat-head-btn" onClick={() => setOpen(false)} title="Fermer" aria-label="Fermer"><BI icon="x-lg" /></button>
              </div>
            </div>

            {/* Fil de discussion */}
            <div className="agentchat-body" ref={scrollRef}>
              {messages.length === 0 && (
                <div className="agentchat-empty">
                  <div className="agentchat-empty-ic"><BI icon="chat-square-text" /></div>
                  <p className="agentchat-empty-h">Comment puis-je aider&nbsp;?</p>
                  <p className="agentchat-empty-p">Demandez un candidat, un besoin, ou lancez une recherche.<br />Ex.&nbsp;: « Trouve-moi des développeurs Java à Genève ».</p>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`agentchat-msg agentchat-msg-${m.role}`}>
                  {m.content && <div className="agentchat-bubble">{m.content}</div>}
                  {m.files && m.files.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                      {m.files.map((f, k) => (
                        <span key={k} title={f.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', maxWidth: '190px', padding: '3px 9px', borderRadius: '7px', background: 'var(--code-bg)', border: '1px solid var(--hair)', fontSize: '11px', fontWeight: 600, color: 'var(--text-2)' }}>
                          <BI icon="paperclip" style={{ fontSize: '10px', flex: 'none' }} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {m.role === 'assistant' && m.pieces && m.pieces.length > 0 && (
                    <div className="agentchat-results">
                      {m.pieces.map((p, k) => (
                        <div key={k} className="agentchat-result" style={{ cursor: 'default' }}>
                          <span className="agentchat-result-ic"><BI icon="file-earmark-text" /></span>
                          <span className="agentchat-result-txt">
                            <span className="agentchat-result-t">{p.titre}</span>
                            {p.sous && <span className="agentchat-result-s">{p.sous}</span>}
                          </span>
                          {/* Le token s'appose au clic — jamais stocké dans le message. */}
                          <button type="button" onClick={() => window.open(avecToken(p.url), '_blank', 'noopener')} title="Voir la pièce"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
                            <BI icon="eye" style={{ fontSize: '11px' }} /> Ouvrir
                          </button>
                          <button type="button" onClick={() => window.open(avecToken(p.download || `${p.url}${p.url.includes('?') ? '&' : '?'}download=1`), '_blank', 'noopener')} title="Télécharger la pièce"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
                            <BI icon="download" style={{ fontSize: '11px' }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.role === 'assistant' && m.results && m.results.length > 0 && (
                    <div className="agentchat-results">
                      {m.results.map((r, k) => (
                        <button key={k} className="agentchat-result"
                          onClick={() => r.kind === 'besoin' ? onOpenBesoin?.(r.id) : onOpenCandidat?.(r.id, { title: r.title, subtitle: r.subtitle, score: r.score })}>
                          <span className="agentchat-result-ic"><BI icon={r.kind === 'besoin' ? 'briefcase' : 'person'} /></span>
                          <span className="agentchat-result-txt">
                            <span className="agentchat-result-t">{r.title}</span>
                            {r.subtitle && <span className="agentchat-result-s">{r.subtitle}</span>}
                          </span>
                          {r.score != null && <span className="agentchat-result-score">{Math.round(r.score)}%</span>}
                          <BI icon="chevron-right" style={{ color: 'var(--text-faint)', fontSize: '13px' }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* L'agent travaille : le nuage tourne, la phrase dit quoi (le libellé
                  de l'outil servi par le serveur, sinon « Réflexion… »). */}
              {(tool || (streaming && attente)) && (
                <div className="agentchat-reflexion" role="status" aria-live="polite">
                  <NuageReflexion taille={40} className="agentchat-nuage" />
                  <span key={tool || 'reflexion'} className="agentchat-reflexion-txt">{tool || 'Réflexion…'}</span>
                </div>
              )}
              {/* Une tâche suivie nom par nom (le serveur envoie `etat` avec la
                  liste) : la barre, puis chaque élément — fait, en cours, à venir,
                  échec. Douze lignes au plus, le reste compté. */}
              {tool && toolEtat && (
                <div className="agentchat-progression" role="progressbar" aria-valuemin={0} aria-valuemax={toolEtat.total || 0} aria-valuenow={toolEtat.done || 0}>
                  <div className="agentchat-progression-barre"><span style={{ width: `${toolEtat.total ? Math.round(((toolEtat.done || 0) / toolEtat.total) * 100) : 0}%` }} /></div>
                  <ul className="agentchat-progression-liste">
                    {toolEtat.elements.slice(0, 12).map((e, i) => (
                      <li key={i} className={`agentchat-progression-ligne is-${e.statut || 'a_venir'}`} title={e.erreur || undefined}>
                        <BI icon={e.statut === 'fait' ? 'check2' : e.statut === 'echec' ? 'x-lg' : e.statut === 'en_cours' ? 'arrow-repeat' : 'dot'} />
                        <span>{e.libelle || e.nom}{e.statut === 'en_cours' ? ' — en cours' : ''}</span>
                      </li>
                    ))}
                    {toolEtat.elements.length > 12 && <li className="agentchat-progression-ligne">… et {toolEtat.elements.length - 12} de plus</li>}
                  </ul>
                </div>
              )}
            </div>

            {/* Pièces jointes en attente d'envoi (supprimables) */}
            {(attachments.length > 0 || uploading) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', padding: '8px 12px 0' }}>
                {attachments.map((f) => (
                  <span key={f.file_id} title={`${f.name} — référence valable 24 h`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '200px', padding: '4px 9px', borderRadius: '8px', background: 'var(--code-bg)', border: '1px solid var(--hair)', fontSize: '11.5px', fontWeight: 600, color: 'var(--text)' }}>
                    <BI icon="paperclip" style={{ fontSize: '10px', flex: 'none', color: 'var(--text-3)' }} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                    {f.size != null && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '9.5px', color: 'var(--text-faint)', flex: 'none' }}>{Math.max(1, Math.round(f.size / 1024))} Ko</span>}
                    <button onClick={() => setAttachments(prev => prev.filter(x => x.file_id !== f.file_id))} title="Retirer"
                      style={{ display: 'inline-flex', border: 'none', background: 'transparent', color: 'var(--text-faint)', cursor: 'pointer', padding: 0 }}><BI icon="x" style={{ fontSize: '12px' }} /></button>
                  </span>
                ))}
                {uploading && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: 'var(--text-3)', padding: '4px 2px' }}><BI icon="arrow-repeat" style={{ animation: 'ats-spin .7s linear infinite' }} /> envoi…</span>}
              </div>
            )}

            {/* Saisie — le drag & drop de fichiers est accepté sur toute la zone */}
            <div className="agentchat-input-row" style={dragOver ? { outline: '2px dashed var(--text-faint)', outlineOffset: '-4px', borderRadius: '10px' } : undefined}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer?.files); }}>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
                onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Joindre des fichiers (max 15 × 15 Mo)"
                style={{ flex: 'none', width: '34px', height: '34px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'transparent', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                <BI icon="paperclip" style={{ fontSize: '14px' }} />
              </button>
              <textarea ref={inputRef} className="agentchat-input" rows={1} value={input}
                onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown}
                placeholder={attachments.length ? 'Un message avec vos fichiers ? (optionnel)' : 'Écrire un message…'} disabled={false} />
              {streaming ? (
                <button className="agentchat-send agentchat-stop" onClick={stop} title="Arrêter"><BI icon="stop-fill" /></button>
              ) : (
                <button className="agentchat-send" onClick={send} disabled={!input.trim() && attachments.length === 0} title="Envoyer"><BI icon="arrow-up" /></button>
              )}
            </div>
          </div>
          {confirmElement}
          {lot && <LotConfirmation input={lot.input} onResolve={lot.resolve} />}
        </>
      )}
    </div>
  );
}
